import { FirebaseApp, getApps, initializeApp } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  MessagePayload,
  Messaging,
  onMessage,
} from "firebase/messaging";
import { getFirebaseConfig, VAPID_KEY } from "../config";
import { NotificationTransformer } from "../shared/notification-transformer";
import type {
  DeviceIdGenerator,
  NotificationHandler,
  PermissionStatus,
  PushNotificationService,
  TokenManager,
} from "../types";

export class FirebasePushService implements PushNotificationService {
  private app: FirebaseApp | null = null;
  private messaging: Messaging | null = null;
  private currentToken: string | null = null;

  constructor(
    private tokenManager: TokenManager,
    private notificationHandler: NotificationHandler,
    private deviceIdGenerator: DeviceIdGenerator
  ) {
    this.initialize();
  }

  private async initialize(): Promise<void> {
    if (!this.isSupported()) {
      console.warn("Firebase messaging not supported in this browser");
      return;
    }

    const config = getFirebaseConfig();
    this.app = getApps().length === 0 ? initializeApp(config) : getApps()[0];
    this.messaging = getMessaging(this.app);

    this.setupMessageListener();
    this.setupPeriodicTokenCheck();
  }

  private setupMessageListener(): void {
    if (!this.messaging) return;

    onMessage(this.messaging, (payload: MessagePayload) => {
      const notification = NotificationTransformer.transform(
        {
          id: payload.data?.notificationId,
          title: payload.notification?.title,
          body: payload.notification?.body,
          data: payload.data,
        },
        "firebase"
      );
      this.notificationHandler.handleNotification(notification);
    });
  }

  async requestPermission(): Promise<boolean> {
    if (!this.messaging || !document.hasFocus()) {
      return false;
    }

    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      const token = await this.getToken();
      return token !== null;
    }
    return false;
  }

  async getToken(): Promise<string | null> {
    if (!this.messaging || Notification.permission !== "granted") {
      return null;
    }

    await this.registerServiceWorker();
    const token = await getToken(this.messaging, { vapidKey: VAPID_KEY });

    if (token) {
      this.currentToken = token;
      return token;
    }
    return null;
  }

  getCurrentToken(): string | null {
    return this.currentToken;
  }

  isSupported(): boolean {
    return "serviceWorker" in navigator && "PushManager" in window;
  }

  async getPermissionStatus(): Promise<PermissionStatus> {
    const permission = Notification.permission;
    return permission === "default" ? "prompt" : (permission as PermissionStatus);
  }

  async onUserLogin(): Promise<void> {
    const permissionStatus = await this.getPermissionStatus();
    if (permissionStatus === "granted") {
      const token = await this.getToken();
      if (token) {
        const deviceId = await this.deviceIdGenerator.generate();
        await this.tokenManager.registerToken(token, "web", deviceId);
      }
    }
  }

  async onUserLogout(): Promise<void> {
    if (this.currentToken && this.messaging) {
      await deleteToken(this.messaging);
      await this.tokenManager.unregisterToken(this.currentToken);
      this.currentToken = null;
    }
  }

  async onAccountSwitch(): Promise<void> {
    if (this.currentToken) {
      const deviceId = await this.deviceIdGenerator.generate();
      await this.tokenManager.registerToken(this.currentToken, "web", deviceId);
    } else {
      await this.onUserLogin();
    }
  }

  private async registerServiceWorker(): Promise<void> {
    if ("serviceWorker" in navigator) {
      try {
        await navigator.serviceWorker.register("/firebase-messaging-sw.js");
      } catch (error) {
        console.error("Service Worker registration failed:", error);
      }
    }
  }

  private setupPeriodicTokenCheck(): void {
    setInterval(
      async () => {
        await this.checkAndUpdateToken();
      },
      30 * 60 * 1000
    ); // 30 minutes
  }

  private async checkAndUpdateToken(): Promise<void> {
    if (!this.messaging || !this.currentToken) return;

    try {
      const currentToken = await getToken(this.messaging, { vapidKey: VAPID_KEY });

      if (currentToken && currentToken !== this.currentToken) {
        await this.tokenManager.unregisterToken(this.currentToken);
        const deviceId = await this.deviceIdGenerator.generate();
        const registered = await this.tokenManager.registerToken(currentToken, "web", deviceId);

        if (registered) {
          this.currentToken = currentToken;
        }
      }
    } catch (error) {
      console.error("Failed to check token update:", error);
    }
  }
}
